import type { IWidgetSession } from "../shared/session/interfaces/session";

/** Backend `cipStatus` value while Kentucky's webhook has not arrived yet. */
const CIP_PENDING_STATUS = "pending";

/**
 * Whether CIP should be skipped for this session's user.
 *
 * Mirrors the backend's `evaluateCipReadiness` short-circuit
 * (`user.cipStatus && user.cipStatus !== 'pending'` → no CIP), and adds the
 * in-session completion flag so a user who just finished KentuckyKYC is not
 * sent back into it while the webhook is still in flight.
 */
export const hasCompletedCip = (session?: IWidgetSession | null): boolean => {
  if (!session) return false;
  if (session.cipCompletedAt) return true;
  const status = session.cipStatus;
  return typeof status === "string" && status !== "" && status !== CIP_PENDING_STATUS;
};

/**
 * Client-known facts the backend may not report (yet). Carried over when a
 * claim replaces `session.data` for the same link, so a page reload does not
 * forget that CIP already ran or that a phone number was captured.
 */
export const carryOverClientSessionFacts = (
  previous: IWidgetSession | null | undefined,
  next: IWidgetSession
): IWidgetSession => {
  if (!previous || previous.linkId !== next.linkId) return next;
  return {
    ...next,
    cipCompletedAt: next.cipCompletedAt ?? previous.cipCompletedAt,
    hasPhoneNumber:
      typeof next.hasPhoneNumber === "boolean"
        ? next.hasPhoneNumber
        : previous.hasPhoneNumber,
  };
};
